import { useAuthStore, useDrawerStore } from '../../../store';
import { useSearchParams } from 'react-router-dom';
import { usePurchaseRequestDetailStore } from '../../../store/PurchaseRequestStore';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { dateFormat, isUserCanCreate } from '../../../lib/utils';
import { useGeneratePurchaseOrder } from '../../../hooks/usePurchaseRequestHooks';
import InfoModal from '../../../components/ui/modal/InfoModal';
import ErrorModal from '../../../components/ui/modal/ErrorModal';
import { Button, Drawer, Form, Space } from 'antd';
import SearchFilter from '../../../components/ui/filters/SearchFilter';
import SubmitButton from '../../../components/ui/SubmitButton';
import CustomForm from '../../../components/ui/CustomForm';
import { FilterOutlined } from '@ant-design/icons';
import { PurchaseRequestDetailFilter } from '../../../types/PurchaseRequest';
import { BaseModel, FormField, PageableResponse } from '../../../types';
import { useFetchData } from '../../../hooks';
import { Resource } from '../../../lib/enums';

const PurchaseRequestDetailFilterHeader = () => {
  const [form] = Form.useForm();
  const drawerStore = useDrawerStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, resetSelectedRowKeys } = usePurchaseRequestDetailStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState('');

  const startDate =
    searchParams.get('from') === undefined || searchParams.get('from') === null
      ? dayjs().subtract(6, 'month').format(dateFormat)
      : searchParams.get('from');

  const endDate =
    searchParams.get('to') === undefined || searchParams.get('to') === null
      ? dayjs().add(2, 'month').format(dateFormat)
      : searchParams.get('to');

  const { user } = useAuthStore();

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const hasSelected = data.length > 0;

  const {
    mutate,
    isPending,
    isError: isUpdateError,
    error: updateError,
    isSuccess,
  } = useGeneratePurchaseOrder();

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isUpdateError) {
      setIsErrorModalOpen(true);
      setErrorMess(updateError?.message);
    }
  }, [isSuccess, isUpdateError]);

  const onClick = () => {
    mutate(data);
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    resetSelectedRowKeys();
  };

  const proder =
    searchParams.get('proder') === undefined ||
    searchParams.get('proder') === null
      ? ''
      : searchParams.get('proder');
  const sku =
    searchParams.get('sku') === undefined || searchParams.get('sku') === null
      ? ''
      : searchParams.get('sku');
  const product =
    searchParams.get('product') === undefined ||
    searchParams.get('product') === null
      ? ''
      : searchParams.get('product');
  const destinationParam =
    searchParams.get('destination') === undefined ||
    searchParams.get('destination') === null
      ? user?.defaultLocation?.id
      : searchParams.get('destination');

  const [filter, setFilter] = useState<PurchaseRequestDetailFilter>({
    from: startDate,
    to: endDate,
    datepicker: [startDate, endDate],
    proder: proder,
    sku: sku,
    product: product,
    destination: destinationParam,
  });

  const formFields: FormField[] = [
    {
      name: 'datepicker',
      label: 'Date coverage',
      rules: [{ required: true, message: 'Please select date coverage' }],
      type: 'date-range-picker',
      allowClear: false,
    },
    {
      name: 'sku',
      label: 'SKU',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'proder',
      label: 'Proder',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'product',
      label: 'Product',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'destination',
      label: 'Department',
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      defaultValue: user?.defaultLocation?.code,
      allowClear: false,
    },
  ];

  const isCurrentUserCanCreatePRD = () => {
    return (
      user?.role === 'ROLE_ADMIN' ||
      isUserCanCreate(user?.moduleAccess, 'Purchase Request Details')
    );
  };

  const isCurrentUserCanCreatePO = () => {
    return (
      user?.role === 'ROLE_ADMIN' ||
      isUserCanCreate(user?.moduleAccess, 'PurchaseOrder')
    );
  };

  const onFinish = (values: {
    datepicker: string;
    product: string;
    proder: string;
    sku: string;
    destination: string;
  }) => {
    console.log(destinationParam);
    const destinationObject: BaseModel | undefined =
      defaultLocationData?.data.find((obj) => {
        return obj.id == values.destination;
      });

    setSearchParams((prev) => {
      prev.set('from', dayjs(values.datepicker[0]).format(dateFormat));
      prev.set('to', dayjs(values.datepicker[1]).format(dateFormat));
      prev.set('proder', values.proder === undefined ? '' : values.proder);
      prev.set('product', values.product === undefined ? '' : values.product);
      prev.set('sku', values.sku === undefined ? '' : values.sku);

      const destinationId = destinationObject?.id ?? (user?.defaultLocation?.id || '');
      prev.set('destination', destinationId);
      return prev;
    });
    setFilter({
      from: dayjs(values.datepicker[0]).format(dateFormat),
      to: dayjs(values.datepicker[1]).format(dateFormat),
      datepicker: [
        dayjs(values.datepicker[0]).format(dateFormat),
        dayjs(values.datepicker[1]).format(dateFormat),
      ],
      product: values.product,
      proder: values.proder,
      sku: values.sku,
      destination: destinationObject?.id ?? user?.defaultLocation?.id ?? '',
    });
    drawerStore.onClose();
  };

  return (
    <>
      <div className={'flex justify-between p-4'}>
        <Space direction="horizontal" size={12}>
          <SearchFilter />
        </Space>

        <div className={'flex space-x-2'}>
          {isCurrentUserCanCreatePRD() && isCurrentUserCanCreatePO() && (
            <Button
              onClick={onClick}
              disabled={!hasSelected}
              loading={isPending}
              style={{
                backgroundColor: !hasSelected ? 'bg-gray-300' : '#A23231',
                borderRadius: '0.75rem',
                color: !hasSelected ? 'text-gray-500' : 'white',
                fontWeight: 'bold',
              }}
            >
              Generate PO
              <span style={{ marginLeft: 8 }}>
                {hasSelected ? `(${data.length})` : ''}
              </span>
            </Button>
          )}
          <Button onClick={drawerStore.onClose} icon={<FilterOutlined />} />
        </div>
      </div>

      <Drawer
        onClose={drawerStore.onClose}
        open={drawerStore.isOpen}
        extra={
          <Space>
            <Button onClick={drawerStore.onClose}>Cancel</Button>
            <SubmitButton form={form}>Filter</SubmitButton>
          </Space>
        }
      >
        <CustomForm
          rowCol={1}
          data={filter}
          form={form}
          formFields={formFields}
          onFinish={onFinish}
          layout={'vertical'}
          formName={'form_drawer'}
        />
      </Drawer>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={`Purchase Order successfully generated`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseRequestDetailFilterHeader;
